<template>
  <div class="p-6 max-w-5xl mx-auto space-y-6">
    <el-card shadow="hover">
      <template #header>
        <div class="flex items-start md:items-center justify-between gap-3 flex-col md:flex-row">
          <h2 class="text-lg font-semibold">{{ isMobile() ? '标准计算' : `标准计算（${form.method === 'EPI' ? '等额本息' : '等额本金'}）`
            }}</h2>
          <div class="flex items-center gap-2 flex-wrap">
            <el-segmented class="EPI mobile-wrap w-full sm:w-auto" v-model="form.method" :options="methodOptions" />
            <el-dropdown class="w-full sm:w-auto" @command="onSelectExample">
              <el-button size="small" plain>
                示例方案选择
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item v-for="ex in examples" :key="ex.key" :command="ex.key">
                    {{ ex.title }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </template>
      <el-alert type="info" :closable="false" class="mb-3"
        :title="isMobile() ? '推荐使用电脑端访问，可以查看公式和计算可视化过程。用途：已知金额、利率、期限，按等额本息/等额本金计算月供/首月、总利息与还款计划，适用于购房、消费分期等标准场景。' : '用途：已知金额、利率、期限，按等额本息/等额本金计算月供/首月、总利息与还款计划，适用于购房、消费分期等标准场景。'" />
      <FormulaPanel :title="form.method === 'EPI' ? '等额本息（EPI）' : '等额本金（EP）'"
        :formulas="form.method === 'EPI' ? epiFormulas : epFormulas" :variables="variablesMap"
        :notes="[`r为月利率（年化/12），金额单位为当前货币（${symbol}）；所有金额保留两位小数。`]" :collapsible="true" :autoCollapseOnMobile="true"
        size="compact" />
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <el-form label-position="top" class="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
          <el-form-item :label="`贷款金额（${symbol}）`">
            <el-input v-model.number="amountUI" type="number" placeholder="如 500000" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PV）</div>
          </el-form-item>
          <el-form-item label="贷款年化利率（%）">
            <el-input v-model.number="form.annualRatePct" type="number" placeholder="如 4.2" />
            <div class="text-xs text-gray-500 mt-1">（此处填写r）</div>
          </el-form-item>
          <el-form-item label="期数（月）">
            <el-input v-model.number="form.months" type="number" placeholder="如 360" />
            <div class="text-xs text-gray-500 mt-1">（此处填写n）</div>
          </el-form-item>
        </el-form>
        <div class="md:col-span-1 flex gap-3 md:justify-end flex-wrap">
          <el-button type="primary" @click="onCalc">计算</el-button>
          <el-button @click="onReset">重置</el-button>
        </div>
      </div>
      <el-alert v-if="error" type="error" class="mt-4" :closable="false" :title="error" />

      <el-collapse-transition>
        <div v-if="result" class="mt-4 space-y-4">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <el-statistic :value="displayTotalPayment" :title="`总还款（${symbol}）`" />
            <el-statistic :value="displayTotalInterest" :title="`总利息（${symbol}）`" />
            <el-statistic v-if="result.method === 'EPI'" :value="displayPMT" :title="`月供（${symbol}）`" />
            <el-statistic v-else :value="displayFirstMonth" :title="`首月还款（${symbol}）`" />
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <el-card shadow="never" body-class="p-0">
              <v-chart class="h-56 md:h-72" :option="chartOption" autoresize />
            </el-card>
            <el-card shadow="never" body-class="p-0">
              <el-table :data="displayRows" height="300" size="small">
                <el-table-column prop="period" label="#" width="60" />
                <el-table-column prop="payment" label="当期还款" />
                <el-table-column prop="principal" label="本金" />
                <el-table-column prop="interest" label="利息" />
                <el-table-column prop="balance" label="剩余本金" />
              </el-table>
            </el-card>
          </div>
        </div>
      </el-collapse-transition>
    </el-card>

    <!-- 示例方案弹窗 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="520px">
      <div class="space-y-3">
        <el-alert type="info" :closable="false" show-icon>
          <template #title>
            {{ dialogTitle }}（示例）
          </template>
          <div class="text-sm text-gray-600 leading-relaxed" v-html="dialogDesc"></div>
        </el-alert>

        <el-form label-position="top" class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <el-form-item :label="`贷款金额（${symbol}）/ PV`">
            <el-input v-model.number="exAmountUI" type="number" />
          </el-form-item>
          <el-form-item label="贷款年化利率（%）/ r">
            <el-input v-model.number="exForm.annualRatePct" type="number" />
          </el-form-item>
          <el-form-item label="期数（月）/ n">
            <el-input v-model.number="exForm.months" type="number" />
          </el-form-item>
          <el-form-item label="还款方式">
            <el-segmented v-model="exForm.method" :options="methodOptions" />
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="applyExample">确认并计算</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { use } from 'echarts/core'
import VChart from 'vue-echarts'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { generateSchedule } from '@/lib/loanEngine'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
import { useCurrency } from '@/composables/useCurrency'
function isMobile() {
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
use([CanvasRenderer, BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent])

const methodOptions = [
  { label: '等额本息', value: 'EPI' },
  { label: '等额本金', value: 'EP' }
]

const form = ref({ amount: 500000, annualRatePct: 4.2, months: 360, method: 'EPI' })
const result = ref(null)
const error = ref('')

// 货币换算
const { symbol, toDisplay, toBase } = useCurrency()
const amountUI = computed({
  get: () => toDisplay(form.value.amount),
  set: (v) => { form.value.amount = toBase(v) }
})

// 示例方案（演示用途，非金融建议）
const examples = ref([
  {
    key: 'bank_mortgage_first_epi',
    title: '首套房按揭 · 等额本息（30年）',
    desc: `
      <ul>
        <li>贷款类型：个人住房按揭（首套）。</li>
        <li>计息与方式：按月计息，等额本息。</li>
        <li>示例利率：年化 4.10%（仅示例，具体以合同为准）。</li>
        <li>期限：30 年（360 期）。</li>
        <li>适用：首套住房长期按揭，现金流稳定，月供恒定便于预算。</li>
      </ul>
    `,
    form: { amount: 1000000, annualRatePct: 4.1, months: 360, method: 'EPI' }
  },
  {
    key: 'consumer_installment_ep',
    title: '消费分期 · 等额本金（24期）',
    desc: `
      <ul>
        <li>贷款类型：耐用品消费分期。</li>
        <li>计息与方式：按月计息，等额本金（前高后低）。</li>
        <li>示例利率：年化 5.50%（仅示例）。</li>
        <li>期限：24 期。</li>
        <li>适用：短期分期，期初利息占比高，逐月下降。</li>
      </ul>
    `,
    form: { amount: 50000, annualRatePct: 5.5, months: 24, method: 'EP' }
  }
])

const dialogVisible = ref(false)
const dialogTitle = ref('')
const dialogDesc = ref('')
const exForm = ref({ amount: 0, annualRatePct: 0, months: 0, method: 'EPI' })
const exAmountUI = computed({ get: () => toDisplay(exForm.value.amount), set: v => exForm.value.amount = toBase(v) })

const onSelectExample = (key) => {
  const ex = examples.value.find(e => e.key === key)
  if (!ex) return
  dialogTitle.value = ex.title
  dialogDesc.value = ex.desc
  exForm.value = { ...ex.form }
  dialogVisible.value = true
}

const applyExample = () => {
  // exForm 中的金额已通过 exAmountUI 映射为基准币
  form.value = { ...exForm.value }
  dialogVisible.value = false
  onCalc()
}

const onCalc = () => {
  error.value = ''
  try {
    result.value = generateSchedule(form.value)
  } catch (e) {
    error.value = e?.message || '计算失败'
    result.value = null
  }
}

const onReset = () => {
  form.value = { amount: 0, annualRatePct: 0, months: 0, method: 'EPI' }
  result.value = null
  error.value = ''
}

const to2 = (v) => Math.round((Number(v) || 0) * 100) / 100
const displayTotalPayment = computed(() => result.value ? to2(toDisplay(result.value.totalPayment)) : 0)
const displayTotalInterest = computed(() => result.value ? to2(toDisplay(result.value.totalInterest)) : 0)
const displayPMT = computed(() => result.value && result.value.method === 'EPI' ? to2(toDisplay(result.value.pmt)) : 0)
const displayFirstMonth = computed(() => result.value && result.value.method === 'EP' ? to2(toDisplay(result.value.firstMonthPayment)) : 0)

const displayRows = computed(() => {
  if (!result.value) return []
  return result.value.rows.map(r => ({
    ...r,
    payment: to2(toDisplay(r.payment)).toFixed(2),
    principal: to2(toDisplay(r.principal)).toFixed(2),
    interest: to2(toDisplay(r.interest)).toFixed(2),
    balance: to2(toDisplay(r.balance)).toFixed(2),
  }))
})

const chartOption = computed(() => {
  if (!result.value) return {}
  const periods = result.value.rows.map(r => r.period)
  const principals = result.value.rows.map(r => toDisplay(r.principal))
  const interests = result.value.rows.map(r => toDisplay(r.interest))
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['本金', '利息'] },
    grid: { left: 24, right: 16, top: 24, bottom: 24, containLabel: true },
    xAxis: { type: 'category', data: periods },
    yAxis: { type: 'value' },
    series: [
      { name: '本金', type: 'bar', stack: 'total', data: principals, emphasis: { focus: 'series' } },
      { name: '利息', type: 'bar', stack: 'total', data: interests, emphasis: { focus: 'series' } },
    ]
  }
})

// 公式与变量映射
const epiFormulas = [
  String.raw`\text{PMT} = \dfrac{PV \cdot r_m (1+r_m)^n}{(1+r_m)^n - 1}`,
  String.raw`\text{Interest}_t = \text{Balance}_{t-1} \cdot r_m,\quad \text{Principal}_t = \text{PMT} - \text{Interest}_t`
]
const epFormulas = [
  String.raw`\text{Principal}_t = \dfrac{PV}{n},\quad \text{Interest}_t = \text{Balance}_{t-1} \cdot r_m,\quad \text{Payment}_t = \text{Principal}_t + \text{Interest}_t`
]
const variablesMap = [
  { symbol: 'PV', label: '贷款金额' },
  { symbol: 'r_m', label: '月利率', desc: '年化利率/12' },
  { symbol: 'n', label: '期数（月）' },
  { symbol: 'PMT', label: '等额本息月供' },
]

</script>

<style scoped>
.EPI.mobile-wrap :deep(.el-segmented__group) {
  flex-wrap: wrap;
  row-gap: 6px;
}

.EPI {
  --el-segmented-item-selected-color: var(--el-text-color-primary);
  --el-segmented-item-selected-bg-color: #ffd100;
  --el-border-radius-base: 16px;
}
</style>
